import { AxiosResponse } from 'axios';
import apis from 'services/apis';
import { IServiceResItem } from 'services/types/response';
import store from 'store';
import storage from 'utils/sessionStorage';

/**
 * Booking-enhance flag of the current shop (`system_conf` APPOINTMENT/BOOKING_ENHANCE,
 * read from shop/all-setting). Off = the booking flow behaves exactly as before the flag existed.
 */
export const isBookingEnhance = () => !!store.getState().shop.setting.bookingEnhance;

export type IBookingContext = {
  timeSlots: string[];
  services: IServiceResItem[];
};

/**
 * Slots are re-fetched today every time the Time tab mounts, so a short TTL keeps the same
 * freshness while letting the Service tab reuse the response instead of making its own call.
 */
const CACHE_TTL_MS = 30_000;
const cache = new Map<string, { at: number; promise: Promise<IBookingContext> }>();

// Scoped by shop: the SPA can switch /:shop_id without a reload, and staff ids are not unique across shops.
const cacheKey = (staffId: string, date: string) => `${storage.shopId.get()}|${staffId || ''}|${date}`;

/**
 * One round trip for everything the booking modal needs after a staff is picked: the free time
 * slots of that staff on `date` (YYYY-MM-DD) and the services they can do. `staffId` empty or '0'
 * means "anyone": shop-wide slots and the full service list. Only used when the flag is on.
 */
export const fetchBookingContext = (staffId: string, date: string): Promise<IBookingContext> => {
  const key = cacheKey(staffId, date);
  const hit = cache.get(key);
  if (hit && Date.now() - hit.at < CACHE_TTL_MS) return hit.promise;

  const promise = apis.getBookingContext(storage.shopId.get(), date, staffId)
    .then((res: AxiosResponse<{ payload: IBookingContext }>) => ({
      timeSlots: res?.data?.payload?.timeSlots || [],
      services: res?.data?.payload?.services || [],
    }))
    .catch((error) => {
      // A failed call must not be served from cache for the next 30 s.
      cache.delete(key);
      throw error;
    });
  cache.set(key, { at: Date.now(), promise });
  return promise;
};

/** Drop cached slots so the next open sees bookings made in the meantime. */
export const clearBookingContextCache = () => cache.clear();
